/**
 * V108-6-4 — "No automated source yet": per program, the objectives no setup step can reach, grouped by
 * suggested owner (server: requirement owner → program lead, the v9.8 assignments model). Each objective
 * has one action ("Add evidence") plus "Send to <owner>" when nobody holds it yet; a group can be sent at
 * once. Sending creates the requirement's owner assignment (shows on their Today). There is no ask
 * tracking (sent / opened) yet, so the screen says "with <name>", never "opened".
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Field, Select, Spinner } from '@aigovops/design-system';
import type { NoSourceGroup } from '@aigovops/core';
import { assignRequirementOwner, getNoSource } from '../../lib/setup-api';
import { personLabel, type Person } from '../../lib/setup-model';
import { programShort } from './parts';
import { setupKeys } from './use-setup';

export function NoSourceList({ slug, prefix, programs, people, viewerId, canAssign, openProgram, onChanged, toast }: {
  slug: string; prefix: string; programs: readonly string[]; people: readonly Person[]; viewerId: string; canAssign: boolean;
  openProgram: string | null; onChanged: () => Promise<unknown>; toast: (m: string, o?: { onUndo?: () => void }) => void;
}) {
  const heading = useRef<HTMLHeadingElement>(null);
  useHashFocus('#no-source', programs.length > 0, () => heading.current);
  if (!programs.length) return null;
  return (
    <section id="no-source" className="su-card" aria-labelledby="su-ns-h" style={{ gap: 'var(--sp-3)' }}>
      <div className="su-card-head"><h3 id="su-ns-h" ref={heading} tabIndex={-1} style={{ margin: 0, fontSize: 'var(--fs-md)' }}>No automated source yet</h3><span className="su-muted" style={{ fontSize: 'var(--fs-body)' }}>These need evidence you add.</span></div>
      <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
        {programs.map((k) => <ProgramNoSource key={k} slug={slug} prefix={prefix} program={k} people={people} viewerId={viewerId} canAssign={canAssign} open={openProgram === k} onChanged={onChanged} toast={toast} />)}
      </div>
    </section>
  );
}

function ProgramNoSource({ slug, prefix, program, people, viewerId, canAssign, open, onChanged, toast }: {
  slug: string; prefix: string; program: string; people: readonly Person[]; viewerId: string; canAssign: boolean; open: boolean;
  onChanged: () => Promise<unknown>; toast: (m: string) => void;
}) {
  const q = useQuery({ queryKey: setupKeys.noSource(slug, program), queryFn: () => getNoSource(slug, program), retry: false, staleTime: 30_000 });
  const [error, setError] = useState('');
  const details = useRef<HTMLDetailsElement>(null);
  // V108-8-8: a link to #nosrc-<program> ("Add GovRAMP evidence by hand") lands here: opened, scrolled to, its summary focused.
  useHashFocus(`#nosrc-${program}`, !!q.data?.no_source.total, () => {
    if (details.current) details.current.open = true;
    return details.current?.querySelector('summary') ?? null;
  });
  if (q.isLoading) return <Spinner label={`Loading ${programShort(program)} objectives`} />;
  if (q.error || !q.data) return <Alert variant="error"><p>{programShort(program)} objectives could not load.</p><Button onClick={() => void q.refetch()}>Retry</Button></Alert>;
  const ns = q.data.no_source;
  if (!ns.total) return null;
  const send = async (ids: string[], userId: string) => {
    setError('');
    try {
      for (const id of ids) await assignRequirementOwner(slug, id, userId);
      await onChanged(); await q.refetch();
      toast(`Sent ${ids.length === 1 ? 'it' : `${ids.length} objectives`} to ${personLabel(userId, viewerId, people) ?? 'them'}. It shows on their Today.`);
    } catch (err) { setError(err instanceof Error ? err.message : 'That did not send. Try again.'); }
  };
  return (
    <details ref={details} className="su-ns" id={`nosrc-${program}`} open={open || undefined}>
      <summary><span style={{ fontWeight: 600 }}>{programShort(program)}</span><span style={{ fontSize: 'var(--fs-lg)', fontWeight: 700 }}>{ns.total}</span>
        <span className="su-muted" style={{ fontSize: 'var(--fs-body)' }}>· {ns.owners} {ns.owners === 1 ? 'owner' : 'owners'} · {ns.asked} with an owner</span></summary>
      <div className="su-ns-body">
        {error && <Alert variant="error">{error}</Alert>}
        {ns.groups.map((g) => <OwnerGroup key={`${g.owner_user_id ?? 'none'}:${g.owner_source}`} g={g} prefix={prefix} program={program} people={people} viewerId={viewerId} canAssign={canAssign} onSend={send} />)}
      </div>
    </details>
  );
}

/**
 * When the URL hash is `hash` and the target is ready, scroll it to the top and move focus there: once per
 * navigation (a router push sets the hash before this list renders, so the browser never scrolls to it).
 */
function useHashFocus(hash: string, ready: boolean, target: () => HTMLElement | null) {
  const location = useLocation();
  const done = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || location.hash !== hash || done.current === location.key) return;
    const el = target();
    if (!el) return;
    done.current = location.key;
    el.scrollIntoView?.({ block: 'start' });
    el.focus();
  });
}

function OwnerGroup({ g, prefix, program, people, viewerId, canAssign, onSend }: {
  g: NoSourceGroup; prefix: string; program: string; people: readonly Person[]; viewerId: string; canAssign: boolean; onSend: (ids: string[], userId: string) => Promise<void>;
}) {
  const name = personLabel(g.owner_user_id, viewerId, people);
  const first = name?.split(' ')[0] ?? null;
  const unheld = g.owner_source !== 'requirement_owner';
  const others = people.filter((p) => p.user_id !== viewerId);
  const [pick, setPick] = useState(g.owner_user_id && g.owner_user_id !== viewerId ? g.owner_user_id : others[0]?.user_id ?? '');
  const ids = g.objectives.map((o) => o.requirement_id);
  const evidenceHref = `${prefix}/programs/${program}/prepare?view=gaps`;
  return (
    <div className="su-owner">
      <div className="su-owner-h">
        <h4>{name ?? 'No owner yet'} <span className="su-muted" style={{ fontWeight: 400 }}>· {g.owner_source === 'requirement_owner' ? 'holds these' : g.owner_source === 'program_lead' ? 'program lead · suggested' : 'choose someone'}</span></h4>
        {canAssign && unheld && (g.owner_user_id && g.owner_user_id !== viewerId
          ? <Button variant="ghost" compact onClick={() => void onSend(ids, g.owner_user_id!)}>Send all {ids.length} to {first}</Button>
          : others.length > 0 && <span className="su-acts" style={{ alignItems: 'end' }}>
            <Field id={`ns-pick-${program}-${g.owner_user_id ?? 'none'}`} label="Send to"><Select value={pick} onChange={(e) => setPick(e.target.value)}>{others.map((p) => <option key={p.user_id} value={p.user_id}>{p.name}</option>)}</Select></Field>
            <Button variant="ghost" compact disabled={!pick} onClick={() => void onSend(ids, pick)}>Send all {ids.length}</Button>
          </span>)}
      </div>
      <ul className="su-obj-list">
        {g.objectives.map((o) => (
          <li key={o.objective_id}>
            <span>{o.title}<span className="su-ref">{o.ref}</span></span>
            <span className="su-obj-acts">
              <Link className="su-btn" data-variant="ghost" to={evidenceHref}>Add evidence<span className="su-sr">: {o.title}</span></Link>
              {!unheld && <span className="su-muted" style={{ fontSize: 'var(--fs-sm)', minHeight: 44, display: 'inline-flex', alignItems: 'center', padding: '0 var(--sp-2)' }}>With {first ?? 'its owner'}</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
